import { useCallback, useEffect, useState } from 'react';
import { Anchor, Badge, Card, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, errorMessage } from '../api/client';
import type { BrandSummary } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { domainAtFault, readDomain } from '../brand/inheritance';
import { DomainsField } from '../components/DomainsField';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { ReadEdit } from '../components/ReadEdit';

/**
 * Every masthead a publisher runs, with enough on each card to know whether it needs attention.
 *
 * The two badges are the point of the list: at a glance, which brands have been themed and which
 * are still taking the publisher's defaults. The republish count is the other: a brand whose notice
 * has moved on from what its live interactions were published with has readers looking at older
 * wording, and nothing else on any screen would say so.
 */
export function Brands() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [brands, setBrands] = useState<readonly BrandSummary[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setProblem(null);
    setBrands(null);
    api
      .brands(orgId)
      .then((body) => setBrands(body.brands))
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  return (
    <>
      <Title order={1} size="h2">
        Brands
      </Title>
      <Text size="sm" c="dimmed" mt="sm" mb="lg">
        Manage your mastheads, websites and brand settings. Each brand can use{' '}
        <Anchor component={Link} to="/settings/themes">
          the defaults for {orgName}
        </Anchor>{' '}
        or have its own theme and consent notice.
      </Text>

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && brands === null && <Loading label="Loading your brands" />}

      {brands !== null && (
        <Stack component="ul" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {brands.map((brand) => (
            <BrandCard key={brand.brand_id} brand={brand} />
          ))}
        </Stack>
      )}

      {brands !== null &&
        (isOwner ? (
          <NewBrandForm orgId={orgId ?? ''} />
        ) : (
          <Text c="dimmed" mt="xl">
            Only an owner can add a brand or change one. Ask an owner of {orgName} if you need a new
            masthead set up.
          </Text>
        ))}
    </>
  );
}

function BrandCard({ brand }: { brand: BrandSummary }) {
  const stale = brand.interactions_needing_republish;

  return (
    <Card className="brand-card" component="li" withBorder radius="md" p="lg">
      <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
        <Group wrap="nowrap" align="center" gap="sm">
          <span className="brand-monogram" aria-hidden="true">
            {brand.name.slice(0, 1)}
          </span>
          <div>
            <Anchor component={Link} to={`/settings/brands/${brand.brand_id}`} fw={600} c="inherit">
              {brand.name}
            </Anchor>
            <Text size="sm" c="dimmed" mt={2}>
              {brand.domains.length === 0
                ? 'No website yet, so nothing on this brand can be published'
                : brand.domains.join(', ')}
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          <Badge size="sm" radius="sm" variant="light" color="gray" tt="none">
            {brand.theme === null ? 'Theme inherited' : 'Own theme'}
          </Badge>
          <Badge size="sm" radius="sm" variant="light" color="gray" tt="none">
            {brand.consent_text === null ? 'Notice inherited' : 'Own notice'}
          </Badge>
        </Group>
      </Group>

      <Text size="sm" c="dimmed" mt="xs">
        {brand.interaction_count === 1
          ? '1 interaction'
          : `${String(brand.interaction_count)} interactions`}
      </Text>

      {stale > 0 && (
        <Group gap="xs" mt={6} align="center" wrap="wrap">
          <Badge size="sm" radius="sm" variant="light" color="coral" tt="none">
            Older consent notice
          </Badge>
          <Text size="sm" c="dimmed">
            {stale === 1 ? '1 live interaction is' : `${String(stale)} live interactions are`}{' '}
            showing readers an older consent notice.{' '}
            <Anchor component={Link} to={`/settings/brands/${brand.brand_id}`} size="sm">
              See which
            </Anchor>
            .
          </Text>
        </Group>
      )}
    </Card>
  );
}

/** What the form holds while somebody is filling it in. */
interface NewBrand {
  readonly name: string;
  readonly domains: string[];
}

/**
 * Adding a brand asks for the two things it cannot exist without: a name and a website.
 *
 * The theme and the notice are not asked for here. A new brand inherits everything, which is
 * almost always right, and the screen for changing that is one click away and explains itself.
 *
 * The form is behind a press rather than sitting open under the list, for the same reason every
 * other settings form is: this screen is read far more often than it is written to, and a screen
 * that opens with an empty form on it is a screen that is asking somebody a question they did not
 * come here to answer.
 */
function NewBrandForm({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<string | null>(null);
  const [domainProblem, setDomainProblem] = useState<{ index: number; message: string } | null>(
    null,
  );
  const [reveal, setReveal] = useState(false);

  async function create(brand: NewBrand): Promise<boolean> {
    setProblem(null);
    setDomainProblem(null);
    const entries = brand.domains.map((entry) => entry.trim()).filter((entry) => entry !== '');
    try {
      const body = await api.createBrand(orgId, { name: brand.name.trim(), domains: entries });
      void navigate(`/settings/brands/${body.brand.brand_id}`);
      return true;
    } catch (error) {
      setReveal(true);
      if (error instanceof ApiError && error.code === 'invalid_domain') {
        const at = domainAtFault(error.message, brand.domains);
        if (at >= 0) setDomainProblem({ index: at, message: error.message });
        else setProblem(error.message);
      } else {
        setProblem(errorMessage(error));
      }
      return false;
    }
  }

  return (
    <Card component="section" withBorder radius="lg" p="lg" mt="xl">
      <Title order={2} size="h3">
        Add a brand
      </Title>
      <Text size="sm" c="dimmed" mt={4}>
        It starts by inheriting the publisher defaults for its theme and its consent notice. You can
        change either of those afterwards.
      </Text>

      {problem !== null && <ErrorNote message={problem} />}

      <ReadEdit<NewBrand>
        noun="the new brand"
        canEdit
        asForm
        open={() => ({ name: '', domains: [''] })}
        openLabel="Add a brand"
        openName="Add a brand"
        saveLabel="Add brand"
        saveName="Add brand"
        busyLabel="Adding..."
        problemsTitle="Before this can be added:"
        check={(brand) => {
          // Called from the press on the button, so this is an event rather than a render.
          const found = newBrandProblems(brand);
          setReveal(found.length > 0);
          return found;
        }}
        save={create}
        form={(brand, set) => (
          <Stack gap="md" mt="md">
            <TextInput
              id="brand-name"
              label="Name"
              description="What you call the masthead, for example The Yorkshire Herald."
              value={brand.name}
              maxLength={200}
              onChange={(event) => set({ ...brand, name: event.currentTarget.value })}
              style={{ maxWidth: '24rem' }}
            />
            <DomainsField
              domains={brand.domains}
              onChange={(domains) => set({ ...brand, domains })}
              canEdit
              serverProblem={domainProblem}
              revealProblems={reveal}
            />
          </Stack>
        )}
      />
    </Card>
  );
}

function newBrandProblems(brand: NewBrand): readonly string[] {
  const entries = brand.domains.map((entry) => entry.trim()).filter((entry) => entry !== '');
  const found: string[] = [];
  if (brand.name.trim() === '') found.push('The brand needs a name, usually the masthead.');
  if (entries.length === 0) {
    found.push('Add the website this brand publishes on, such as herald.co.uk.');
  }
  for (const entry of entries) {
    const verdict = readDomain(entry);
    if (!verdict.ok) found.push(verdict.reason);
  }
  return found;
}
